import React from "react";
import { useNavigate } from "react-router-dom";
import apedBetaLogo from "./assets/alpha_logo.svg";
import { PrivyProvider, usePrivy } from "@privy-io/react-auth";

const Alpha = () => {
  const navigate = useNavigate();

  const handleLaunchClick = () => {
    navigate("/");
  };
  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-black" />

      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-purple-600/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-purple-600/5 to-transparent rounded-full blur-2xl" />
      </div>

      <div className="relative z-10 min-h-screen flex justify-center items-center">
        <div
          className="absolute top-6 left-6 z-10 cursor-pointer"
          onClick={handleLaunchClick}
        >
          <div className="flex items-center space-x-3">
            <img
              className="w-8 h-8 md:w-10 md:h-10"
              src={apedBetaLogo}
              alt="APED"
            />
            <span className="text-white font-bold text-xl md:text-2xl">
              ← Back
            </span>
          </div>
        </div>
        <div className="flex justify-center flex-col items-center mt-[-50px] z-20 max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              <span className="bg-gradient-to-r from-white via-purple-200 to-purple-400 bg-clip-text text-transparent">
                Welcome to APED Alpha
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Connect your wallet to access the most advanced perpetual
              aggregator on HyperLiquid. Experience lightning-fast trades with
              optimal pricing.
            </p>
          </div>

          <PrivyProvider
            appId="cm18a4iq9037h9n5ocnqxtty2"
            config={{
              appearance: {
                accentColor: "#8B5CF6",
                theme: "dark",
                showWalletLoginFirst: false,
                logo: "https://svgur.com/i/1A_G.svg",
              },
              loginMethods: ["wallet", "email"],
              fundingMethodConfig: {
                moonpay: { useSandbox: true },
              },
              embeddedWallets: {
                createOnLogin: "users-without-wallets",
                requireUserPasswordOnCreate: false,
              },
              mfa: {
                noPromptOnMfaRequired: false,
              },
            }}
          >
            <LoginButton />
          </PrivyProvider>

          {/* Features Preview */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-4xl">
            <div className="p-4 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl text-center">
              <div className="text-2xl mb-2">⚡</div>
              <h3 className="text-white font-semibold mb-1">
                Instant Execution
              </h3>
              <p className="text-gray-400 text-sm">
                Sub-millisecond trade execution
              </p>
            </div>
            <div className="p-4 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl text-center">
              <div className="text-2xl mb-2">🎯</div>
              <h3 className="text-white font-semibold mb-1">Best Prices</h3>
              <p className="text-gray-400 text-sm">
                Aggregated liquidity optimization
              </p>
            </div>
            <div className="p-4 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl text-center">
              <div className="text-2xl mb-2">🔒</div>
              <h3 className="text-white font-semibold mb-1">Secure Trading</h3>
              <p className="text-gray-400 text-sm">
                Non-custodial wallet integration
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoginButton = () => {
  const { login, user } = usePrivy();

  return user ? (
    <div className="text-center p-6 bg-gray-900/50 backdrop-blur-sm border border-purple-500/50 rounded-2xl">
      <div className="text-green-400 text-4xl mb-4">✅</div>
      <h3 className="text-white text-xl font-bold mb-2">Connected!</h3>
      <p className="text-gray-300">
        Welcome,{" "}
        {user.email ||
          user.wallet?.address?.slice(0, 6) +
            "..." +
            user.wallet?.address?.slice(-4) ||
          "User"}
        !
      </p>
      <p className="text-purple-400 text-sm mt-2">
        Ready to trade on HyperLiquid
      </p>
    </div>
  ) : (
    <button
      onClick={login}
      className="group relative inline-flex items-center justify-center px-8 py-4 md:px-12 md:py-6 text-lg md:text-xl font-bold text-white bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl transition-all duration-300 hover:from-purple-500 hover:to-purple-600 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/25 uppercase"
    >
      <span className="absolute inset-0 bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl blur opacity-75 group-hover:opacity-100 transition-opacity" />
      <span className="relative flex items-center space-x-3">
        <span>Connect Wallet</span>
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      </span>
    </button>
  );
};

export default Alpha;
